import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import ApplyVisaSection from '../components/ApplyVisaSection';
import VisaApplicationsPoolSection from '../components/VisaApplicationsPoolSection';
import VisaApplicationHistoryModal from '../components/VisaApplicationHistoryModal';
import VisaApplicationDetailModal from '../components/VisaApplicationDetailModal';
import VisaApplicationFormModal from '../components/VisaApplicationFormModal';
import ForceChangePasswordModal from '../components/ForceChangePasswordModal';
import { DEFAULT_VISA_FILTERS } from '../utils/visaApplicationFilters';
import { getVisaHistoryUrl } from '../utils/visaApplicationHistoryApi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PATH_TO_TAB = {
  '/company-employee/dashboard': 'dashboard',
  '/company-employee/apply-visa': 'apply_visa',
  '/company-employee/my-applications': 'my_applications',
};

const CompanyEmployeeDashboard = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const getTabFromPath = () => PATH_TO_TAB[location.pathname] || 'dashboard';
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  const [profile, setProfile] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(
    Boolean(user?.must_change_password)
  );
  const [stats, setStats] = useState(null);
  const [visaApplications, setVisaApplications] = useState([]);
  const [visaFilters, setVisaFilters] = useState(DEFAULT_VISA_FILTERS);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [formApplication, setFormApplication] = useState(null);
  const [historyApplication, setHistoryApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const init = async () => {
      const tab = getTabFromPath();
      setActiveTab(tab);
      await fetchDashboard();
      if (tab === 'my_applications') await fetchApplications();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    const tab = getTabFromPath();
    setActiveTab(tab);
    if (tab === 'my_applications') fetchApplications();
  }, [location.pathname]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/company-employee/dashboard`);
      setProfile(res.data.profile);
      setStats(res.data);
      if (res.data.profile?.must_change_password != null) {
        setMustChangePassword(Boolean(res.data.profile.must_change_password));
      }
    } catch {
      setError('Failed to load dashboard');
    }
  };

  const fetchApplications = async () => {
    try {
      const res = await axios.get(`${API_URL}/company-employee/visa-applications`);
      setVisaApplications(res.data || []);
    } catch {
      setError('Failed to load your visa applications');
    }
  };

  const renderDashboard = () => (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Welcome, {profile?.name || user?.name}</h1>
      <p className="text-gray-500 mt-1">{profile?.company_name || 'Company Employee Portal'}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <p className="text-sm text-gray-500">My Applications</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.totalApplications ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Company</p>
          <p className="text-xl font-semibold text-gray-900 mt-2">{profile?.company_name || '-'}</p>
        </div>
      </div>
    </div>
  );

  const renderMyApplications = () => (
    <>
      <VisaApplicationsPoolSection
        title="My Visa Applications"
        subtitle="Track applications and complete or update the visa form"
        applications={visaApplications}
        filters={visaFilters}
        onFiltersChange={setVisaFilters}
        onClearFilters={() => setVisaFilters(DEFAULT_VISA_FILTERS)}
        onView={setSelectedApplication}
        onFillForm={setFormApplication}
        onViewHistory={setHistoryApplication}
        hideStatusFilter={false}
        emptyMessage="You have not submitted any visa applications yet."
      />
    </>
  );

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
        )}

        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'apply_visa' && <ApplyVisaSection onSubmitted={fetchApplications} />}
        {activeTab === 'my_applications' && renderMyApplications()}

        {selectedApplication && (
          <VisaApplicationDetailModal
            application={selectedApplication}
            onClose={() => setSelectedApplication(null)}
          />
        )}

        {formApplication && (
          <VisaApplicationFormModal
            application={formApplication}
            onClose={() => setFormApplication(null)}
            onSaved={fetchApplications}
          />
        )}

        {historyApplication && (
          <VisaApplicationHistoryModal
            application={historyApplication}
            historyUrl={getVisaHistoryUrl(historyApplication.id, user?.role)}
            onClose={() => setHistoryApplication(null)}
          />
        )}

        {mustChangePassword && (
          <ForceChangePasswordModal
            endpoint="/company-employee/change-password"
            onSuccess={() => {
              setMustChangePassword(false);
              updateUser({ must_change_password: false });
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default CompanyEmployeeDashboard;
